import {
  ConfirmedProxyMode,
  environmentHeader,
  headersFor,
  normalizeCidr,
  WebEnvironmentSetting,
  type ConfigStatusSummary,
  type DetectedDomain,
  type DetectedWebServer,
  type DomainDns,
  type DomainFront,
  type ModuleHealth,
  type ProxyHeader,
  type ProxyMode,
  type PublishedRangesStatus,
  type ServerWebEnvironment,
  type WebEnvironmentHint,
  type WebEnvironmentProposal,
  type WebEnvironmentSettingInput,
} from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { needsReauth } from '../api/client.js';
import {
  confirmWebEnvironment,
  getWebEnvironment,
  withdrawWebEnvironment,
} from '../api/servers.js';
import { useReauth } from '../auth/reauth-context.js';
import { useCurrentUser } from '../auth/session-context.js';
import { formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { SelectField, TextAreaField } from '../ui/Field.js';
import { ConfirmDialog } from './ConfirmDialog.js';

/**
 * The web environment, on a server's page (knowledge doc sections 8.8, 11 and 12).
 *
 * What the agent found in front of this server's websites, and what an admin
 * confirmed. Detection only proposes (rule 20): nothing here changes a web server
 * until an admin confirms, and confirming is section 12's High row — the password
 * is asked for first — because every address trusted here may tell the server who
 * the visitor is, and is never banned (rules 15 and 16).
 *
 * Confirming makes the agent write Bartizo's include files: real-IP trust and a
 * JSON access log. The one change to an admin's own files is one marked line in
 * each Nginx site ticked here, and only a site with an `access_log` of its own
 * needs it; the rest inherit Bartizo's server-wide log.
 */

const MODE_LABEL: Record<ProxyMode, string> = {
  direct: 'Directly, with no proxy in front',
  cloudflare: 'Through Cloudflare',
  load_balancer: 'Through a load balancer or reverse proxy',
  cloudflare_plus_lb: 'Through Cloudflare, then a load balancer',
  proxy_protocol: 'Through a load balancer using the PROXY protocol',
  unknown: 'Could not tell',
};

const HEADER_LABEL: Record<ProxyHeader, string> = {
  'CF-Connecting-IP': 'CF-Connecting-IP',
  'X-Forwarded-For': 'X-Forwarded-For',
  proxy_protocol: 'the PROXY protocol header',
};

const POSITION_LABEL: Record<DetectedWebServer['position'], string> = {
  edge: 'answers visitors, passes some to a web server behind it',
  backend: 'behind the other web server on this machine',
  standalone: 'answers visitors',
};

const BEHIND_CLOUDFLARE = new Set<ProxyMode>(['cloudflare', 'cloudflare_plus_lb']);

/** Where a domain's DNS sends visitors (P6.7). */
const DNS_LABEL: Record<DomainDns, string> = {
  cloudflare: 'Cloudflare (proxied)',
  direct: 'this server (DNS only)',
  elsewhere: 'another address',
  unknown: 'no answer',
};

const FRONT_LABEL: Record<DomainFront, string> = {
  cloudflare: 'Through Cloudflare',
  direct: 'Directly',
};

/**
 * The front to offer for a domain: what its DNS says when that is clear, else what
 * most of its requests did, else Cloudflare — the server's own mode, so a domain
 * nobody has looked at keeps being watched for a bypass.
 */
function proposedFront(domain: DetectedDomain): DomainFront {
  if (domain.dns === 'cloudflare' || domain.dns === 'direct') return domain.dns;
  if (domain.requests > 0) {
    return domain.viaCloudflare * 2 >= domain.requests ? 'cloudflare' : 'direct';
  }
  return 'cloudflare';
}
const NEEDS_OWN_RANGES = new Set<ProxyMode>([
  'load_balancer',
  'proxy_protocol',
  'cloudflare_plus_lb',
]);

const percent = (value: number): string => `${String(Math.round(value * 100))}%`;

/** A site id is `file|first name|first port`; the file is what an admin looks for. */
const fileOf = (siteId: string): string => siteId.split('|')[0] ?? siteId;

/** Shorter where every file shares the prefix: `sites-enabled/shop`. */
const shortFile = (file: string): string => file.replace(/^\/etc\/nginx\//, '');

/**
 * A server name as an admin reads it. Nginx's `_` is the usual catch-all default
 * server, and a leading dot answers for the name and every subdomain of it;
 * Forge writes both beside each site.
 */
function nameLabel(name: string): string {
  if (name === '_') return 'default server (_)';
  if (name.startsWith('.')) return `${name.slice(1)} and its subdomains`;
  return name;
}

type DetectedSite = DetectedWebServer['sites'][number];

const siteNames = (site: DetectedSite): string =>
  site.serverNames.map(nameLabel).join(', ') || 'no server_name';

/** What tells one block from the next in the same file: its ports, HTTPS, its own log. */
function siteDetails(site: DetectedSite): string {
  const parts: string[] = [];
  if (site.ports.length > 0) {
    parts.push(`${site.ports.length === 1 ? 'port' : 'ports'} ${site.ports.join(', ')}`);
  }
  if (site.ssl) parts.push('HTTPS');
  if (site.hasOwnAccessLog) parts.push('keeps its own access log');
  if (site.includeInstalled) parts.push('Bartizo’s line already in');
  return parts.join(' · ');
}

/** Blocks under the file they live in, in the order Nginx listed them. */
function byFile(sites: DetectedSite[]): { file: string; sites: DetectedSite[] }[] {
  const groups = new Map<string, DetectedSite[]>();
  for (const site of sites) {
    const file = fileOf(site.id);
    groups.set(file, [...(groups.get(file) ?? []), site]);
  }
  return [...groups].map(([file, grouped]) => ({ file, sites: grouped }));
}

/** A confirmed site, from its id alone: the proposal it came from may be gone. */
function managedSiteLabel(siteId: string): string {
  const [, name, port] = siteId.split('|');
  if (name === undefined || name === '') return '';
  return `${nameLabel(name)}${port === undefined || port === '' ? '' : ` on port ${port}`}`;
}

/**
 * Nginx sites Bartizo may add its one marked line to, on a server visitors reach:
 * its log for a site that keeps its own `access_log` (P6.1), and its bot blocks,
 * which Nginx takes only inside each site (P6.6).
 */
function sitesNeedingLine(proposal: WebEnvironmentProposal | null) {
  return (proposal?.servers ?? [])
    .filter((server) => server.type === 'nginx' && server.managed && server.position !== 'backend')
    .flatMap((server) => server.sites);
}

export function WebEnvironmentCard({
  serverId,
  hasWebRole,
  configStatus,
  health,
  timezone,
}: {
  serverId: string;
  /** Section 7's `web` role turns detection on; without it the card appears only once there is something to show. */
  hasWebRole: boolean;
  configStatus: ConfigStatusSummary | null;
  /** `moduleHealth.web_env` from the last heartbeat. */
  health: ModuleHealth | undefined;
  timezone: string;
}): ReactNode {
  const session = useCurrentUser();
  const isAdmin = session.user.role === 'admin';
  const queryClient = useQueryClient();
  const { ensureReauthenticated } = useReauth();
  const [dialog, setDialog] = useState<'confirm' | 'withdraw' | null>(null);

  const query = useQuery({
    queryKey: ['web-environment', serverId],
    queryFn: () => getWebEnvironment(serverId),
  });

  const saved = (view: ServerWebEnvironment): void => {
    queryClient.setQueryData(['web-environment', serverId], view);
    // A new settings version: the rollout state and the history move with it.
    void queryClient.invalidateQueries({ queryKey: ['server', serverId] });
    void queryClient.invalidateQueries({ queryKey: ['config-versions', serverId] });
    void queryClient.invalidateQueries({ queryKey: ['config-layer'] });
    setDialog(null);
  };

  const open = async (which: 'confirm' | 'withdraw'): Promise<void> => {
    // Asked before the dialog rather than after the API refuses, as removing a server does.
    if (await ensureReauthenticated()) setDialog(which);
  };

  if (query.isPending) return null;
  if (query.isError) {
    return hasWebRole ? (
      <Card title="Web environment">
        <Alert tone="danger" title="Could not load the web environment">
          Try again in a moment.
        </Alert>
      </Card>
    ) : null;
  }

  const { proposal, receivedAt, confirmed, cloudflare, hints } = query.data;
  if (!hasWebRole && proposal === null && confirmed === null) return null;

  return (
    <Card
      title="Web environment"
      description="What sits in front of this server’s websites, and which proxies may tell it the visitor’s real address."
      aside={
        confirmed === null ? (
          <span className="badge badge-muted">Not confirmed</span>
        ) : (
          <span className="badge badge-success">Confirmed</span>
        )
      }
      footer={
        isAdmin && (
          <span className="row">
            <Button variant="primary" onClick={() => void open('confirm')}>
              {confirmed === null ? 'Confirm the environment' : 'Change it'}
            </Button>
            {confirmed !== null && (
              <Button variant="danger" onClick={() => void open('withdraw')}>
                Stop managing
              </Button>
            )}
          </span>
        )
      }
    >
      {confirmed === null ? (
        <p className="muted text-sm">
          Nothing is confirmed, so Bartizo has changed nothing in this server’s web configuration
          and trusts no forwarding header here.
          {isAdmin ? ' Check what the agent found below, then confirm it.' : ''}
        </p>
      ) : (
        <Confirmed
          confirmed={confirmed}
          cloudflare={cloudflare}
          configStatus={configStatus}
          health={health}
        />
      )}

      {confirmed !== null &&
        proposal !== null &&
        proposal.mode !== 'unknown' &&
        proposal.mode !== confirmed.mode && (
          <Alert tone="warning" title="The agent now sees something else">
            It proposes “{MODE_LABEL[proposal.mode]}”, but “{MODE_LABEL[confirmed.mode]}” is
            confirmed. If the setup in front of this server changed, confirm it again.
          </Alert>
        )}

      <Hints hints={hints} />

      <h3 className="subhead">What the agent found</h3>
      {proposal === null ? (
        <p className="muted text-sm">
          Nothing yet. The agent looks at its web servers and the connections reaching them when it
          starts, and every six hours after that.
        </p>
      ) : (
        <Proposal proposal={proposal} receivedAt={receivedAt} timezone={timezone} />
      )}

      <CloudflareLine status={cloudflare} />

      {dialog === 'confirm' && (
        <ConfirmEnvironmentDialog
          serverId={serverId}
          proposal={proposal}
          confirmed={confirmed}
          cloudflare={cloudflare}
          onSaved={saved}
          onCancel={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'withdraw' && (
        <WithdrawDialog
          serverId={serverId}
          onSaved={saved}
          onCancel={() => {
            setDialog(null);
          }}
        />
      )}
    </Card>
  );
}

/** What the last day of traffic shows that the setup does not explain: advice, not a setting. */
function Hints({ hints }: { hints: readonly WebEnvironmentHint[] }): ReactNode {
  if (hints.length === 0) return null;
  return (
    <>
      <h3 className="subhead">What the traffic shows</h3>
      {hints.map((hint) => (
        <Alert
          key={hint.code}
          tone={hint.level === 'warning' ? 'warning' : 'info'}
          title={hint.title}
        >
          {hint.detail}
          {hint.addresses.length > 0 && (
            <>
              {' '}
              Busiest: <code>{hint.addresses.join(', ')}</code>.
            </>
          )}
        </Alert>
      ))}
    </>
  );
}

function Confirmed({
  confirmed,
  cloudflare,
  configStatus,
  health,
}: {
  confirmed: WebEnvironmentSetting;
  cloudflare: PublishedRangesStatus | null;
  configStatus: ConfigStatusSummary | null;
  health: ModuleHealth | undefined;
}): ReactNode {
  const header = environmentHeader(confirmed);
  const fronts = Object.entries(confirmed.domains ?? {});
  return (
    <>
      <dl className="details">
        <dt>Visitors arrive</dt>
        <dd>{MODE_LABEL[confirmed.mode]}</dd>
        <dt>Real address from</dt>
        <dd>
          {header === undefined ? (
            <span className="muted">nothing: the connection’s own address is the visitor’s</span>
          ) : (
            HEADER_LABEL[header]
          )}
        </dd>
        <dt>Trusted proxies</dt>
        <dd>
          {confirmed.trusted_proxies_ref === undefined &&
            confirmed.trusted_proxies.length === 0 && <span className="muted">none</span>}
          {confirmed.trusted_proxies_ref === 'cloudflare' && (
            <span className="cell-sub">
              Cloudflare’s published ranges
              {cloudflare === null ? '' : ` (${String(cloudflare.count)})`}, kept current by Bartizo
            </span>
          )}
          {confirmed.trusted_proxies.map((range) => (
            <code key={range} className="cell-sub">
              {range}
            </code>
          ))}
        </dd>
        <dt>Sites Bartizo may add its line to</dt>
        <dd>
          {confirmed.managed_sites.length === 0 ? (
            <span className="muted">none</span>
          ) : (
            confirmed.managed_sites.map((site) => (
              <span key={site} className="cell-sub">
                {managedSiteLabel(site)} <code>{fileOf(site)}</code>
              </span>
            ))
          )}
        </dd>
        {fronts.length > 0 && (
          <>
            <dt>Domains</dt>
            <dd>
              {fronts.map(([name, front]) => (
                <span key={name} className="cell-sub">
                  {name}: {FRONT_LABEL[front].toLowerCase()}
                </span>
              ))}
            </dd>
          </>
        )}
      </dl>
      {fronts.some(([, front]) => front === 'direct') && (
        <p className="muted text-sm">
          Some domains here are reached directly and some through Cloudflare. A ban stops an address
          at this server’s firewall, which reaches visitors of the direct domains; visitors coming
          through Cloudflare connect from Cloudflare’s addresses, so stopping them takes a web-layer
          bouncer or Cloudflare’s own rules. Requests that go around Cloudflare to a domain behind
          it are reported per domain.
        </p>
      )}
      <ApplyStatus configStatus={configStatus} health={health} />
    </>
  );
}

/**
 * Whether the server carries it out. A web server that refuses the new
 * configuration fails the settings version (section 11, steps 5 and 6), so the
 * rollout state says it; a missing helper does not, so the module's health does.
 */
function ApplyStatus({
  configStatus,
  health,
}: {
  configStatus: ConfigStatusSummary | null;
  health: ModuleHealth | undefined;
}): ReactNode {
  if (configStatus?.state === 'failed') {
    return (
      <Alert
        tone="danger"
        title={`Settings version ${String(configStatus.latestVersion ?? '')} failed on this server`}
      >
        {configStatus.detail ?? 'The agent gave no reason.'} It is still running the last version
        that worked.
      </Alert>
    );
  }
  if (configStatus?.state === 'pending') {
    return (
      <p className="muted text-sm">
        Waiting for the server to take settings version {String(configStatus.latestVersion ?? '')}.
      </p>
    );
  }
  if (health !== undefined && !health.ok) {
    return (
      <Alert tone="warning" title="Not fully applied">
        {health.detail ?? 'The web environment module reports a problem.'}
      </Alert>
    );
  }
  return null;
}

function Proposal({
  proposal,
  receivedAt,
  timezone,
}: {
  proposal: WebEnvironmentProposal;
  receivedAt: string | null;
  timezone: string;
}): ReactNode {
  return (
    <>
      <dl className="details">
        <dt>Visitors seem to arrive</dt>
        <dd>
          {MODE_LABEL[proposal.mode]}
          <span className="cell-sub">{percent(proposal.confidence)} confident</span>
        </dd>
        <dt>Web servers</dt>
        <dd>
          {proposal.servers.length === 0 ? (
            <span className="muted">none found</span>
          ) : (
            proposal.servers.map((server) => (
              <span key={`${server.name}-${server.ports.join(',')}`} className="cell-sub">
                {server.name}
                {server.version === undefined ? '' : ` ${server.version}`} on{' '}
                {server.ports.join(', ') || 'no port'}: {POSITION_LABEL[server.position]}
                {server.managed ? '' : ' — not managed by Bartizo'}
                {server.sites.length > 0 &&
                  `, ${String(server.sites.length)} site${server.sites.length === 1 ? '' : 's'}`}
              </span>
            ))
          )}
        </dd>
        <dt>Never banned now</dt>
        <dd>
          {proposal.allowlisted.length === 0
            ? 'nothing'
            : `${String(proposal.allowlisted.length)} range${proposal.allowlisted.length === 1 ? '' : 's'}`}
          <span className="cell-sub">
            {proposal.allowlistApplied
              ? 'On CrowdSec’s allowlist here: Cloudflare’s ranges, private peers and real-IP trust the web server already had.'
              : 'Not on CrowdSec’s allowlist yet: the root helper could not set it.'}
          </span>
        </dd>
        <dt>Connections looked at</dt>
        <dd>{proposal.sampledConnections.toLocaleString('en-US')}</dd>
        {receivedAt !== null && (
          <>
            <dt>Reported</dt>
            <dd>
              {formatRelative(receivedAt)}
              <span className="cell-sub">{formatInstant(receivedAt, { timezone })}</span>
            </dd>
          </>
        )}
      </dl>
      {proposal.evidence.length > 0 && (
        <ul className="text-sm">
          {proposal.evidence.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      {proposal.domains.length > 0 && <Domains domains={proposal.domains} />}
    </>
  );
}

/** Each domain's DNS and its requests (P6.7): how to tell which are behind Cloudflare. */
function Domains({ domains }: { domains: DetectedDomain[] }): ReactNode {
  return (
    <div className="table-wrap">
      <table className="table">
        <caption>Domains</caption>
        <thead>
          <tr>
            <th scope="col">Domain</th>
            <th scope="col">DNS points to</th>
            <th scope="col">Requests</th>
            <th scope="col">Through Cloudflare</th>
          </tr>
        </thead>
        <tbody>
          {domains.map((domain) => (
            <tr key={domain.name}>
              <td>{domain.name}</td>
              <td>
                {DNS_LABEL[domain.dns]}
                {domain.addresses.length > 0 && (
                  <span className="cell-sub">{domain.addresses.join(', ')}</span>
                )}
              </td>
              <td>{domain.requests.toLocaleString('en-US')}</td>
              <td>
                {domain.requests === 0 ? (
                  <span className="muted">no requests yet</span>
                ) : (
                  percent(domain.viaCloudflare / domain.requests)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CloudflareLine({ status }: { status: PublishedRangesStatus | null }): ReactNode {
  if (status === null) return null;
  return (
    <p className="muted text-sm">
      Cloudflare’s ranges: {String(status.count)},{' '}
      {status.origin === 'fetched' && status.fetchedAt !== null
        ? `fetched ${formatRelative(status.fetchedAt)}`
        : 'from the copy Bartizo ships with, not fetched yet'}
      .{status.lastError !== null && ` The last fetch failed: ${status.lastError}`}
    </p>
  );
}

/** One per line, as typed; each becomes one spelling of a range, or an error naming the line. */
function parseRanges(text: string): { ranges: string[]; bad: string[] } {
  const ranges: string[] = [];
  const bad: string[] = [];
  for (const line of text.split(/[\s,]+/)) {
    if (line === '') continue;
    const range = normalizeCidr(line);
    if (range === undefined) bad.push(line);
    else if (!ranges.includes(range)) ranges.push(range);
  }
  return { ranges, bad };
}

function ConfirmEnvironmentDialog({
  serverId,
  proposal,
  confirmed,
  cloudflare,
  onSaved,
  onCancel,
}: {
  serverId: string;
  proposal: WebEnvironmentProposal | null;
  confirmed: WebEnvironmentSetting | null;
  cloudflare: PublishedRangesStatus | null;
  onSaved: (view: ServerWebEnvironment) => void;
  onCancel: () => void;
}): ReactNode {
  const proposed = proposal?.mode === 'unknown' ? undefined : proposal?.mode;
  const [mode, setMode] = useState<ConfirmedProxyMode>(confirmed?.mode ?? proposed ?? 'direct');
  // The mode's default is the empty choice, so a stored default shows as that.
  const [header, setHeader] = useState<ProxyHeader | ''>(
    confirmed?.header === undefined || confirmed.header === headersFor(confirmed.mode)[0]
      ? ''
      : confirmed.header,
  );
  const [rangesText, setRangesText] = useState(
    (confirmed?.trusted_proxies ?? proposal?.trustedProxies ?? []).join('\n'),
  );
  const candidates = sitesNeedingLine(proposal);
  const idBase = useId();
  // Every domain the agent found or an admin confirmed, each with a front to confirm (P6.7).
  const domainNames = [
    ...new Set([
      ...(proposal?.domains ?? []).map((domain) => domain.name),
      ...Object.keys(confirmed?.domains ?? {}),
    ]),
  ];
  const [fronts, setFronts] = useState<Record<string, DomainFront>>(() =>
    Object.fromEntries(
      domainNames.map((name) => {
        const found = proposal?.domains.find((domain) => domain.name === name);
        return [
          name,
          confirmed?.domains?.[name] ?? (found === undefined ? 'cloudflare' : proposedFront(found)),
        ];
      }),
    ),
  );
  const [sites, setSites] = useState<string[]>(
    confirmed?.managed_sites ?? candidates.map((site) => site.id),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (environment: WebEnvironmentSettingInput) =>
      confirmWebEnvironment(serverId, environment),
    onSuccess: onSaved,
    onError: (caught: unknown) => {
      setError(
        needsReauth(caught)
          ? 'That needed a fresh confirmation. Close this and try again.'
          : caught instanceof Error
            ? caught.message
            : 'Could not confirm it.',
      );
    },
  });

  const ownRanges = NEEDS_OWN_RANGES.has(mode);
  const choices = headersFor(mode);

  const submit = (): void => {
    setError(null);
    const { ranges, bad } = ownRanges ? parseRanges(rangesText) : { ranges: [], bad: [] };
    if (bad.length > 0) {
      setError(`Not an address or range: ${bad.slice(0, 3).join(', ')}`);
      return;
    }
    const input: WebEnvironmentSettingInput = {
      mode,
      trusted_proxies: ranges,
      ...(BEHIND_CLOUDFLARE.has(mode) ? { trusted_proxies_ref: 'cloudflare' as const } : {}),
      ...(header !== '' && choices.includes(header) ? { header } : {}),
      managed_sites: sites.filter((id) => candidates.some((site) => site.id === id)),
      // Only a mixed server needs fronts: all through Cloudflare is the mode itself.
      ...(BEHIND_CLOUDFLARE.has(mode) && Object.values(fronts).includes('direct')
        ? { domains: fronts }
        : {}),
    };
    // The API checks the same schema; asking first puts the reason next to the field.
    const parsed = WebEnvironmentSetting.safeParse(input);
    if (!parsed.success) {
      setError(parsed.error.issues.map((issue) => issue.message).join('; '));
      return;
    }
    save.mutate(input);
  };

  return (
    <ConfirmDialog
      title="Confirm the web environment"
      description="What you confirm here decides which addresses may tell this server who its visitors are."
      confirmLabel="Confirm and apply"
      busy={save.isPending}
      error={error}
      onConfirm={submit}
      onCancel={onCancel}
    >
      <SelectField
        label="How visitors reach this server"
        hint={proposed === undefined ? undefined : `The agent proposes: ${MODE_LABEL[proposed]}.`}
        value={mode}
        onChange={(event) => {
          setMode(ConfirmedProxyMode.parse(event.target.value));
          setHeader('');
        }}
      >
        {ConfirmedProxyMode.options.map((option) => (
          <option key={option} value={option}>
            {MODE_LABEL[option]}
          </option>
        ))}
      </SelectField>

      {BEHIND_CLOUDFLARE.has(mode) && (
        <p className="text-sm">
          Cloudflare’s published ranges are trusted
          {cloudflare === null ? '' : ` (${String(cloudflare.count)} of them)`}, and Bartizo keeps
          them current.
        </p>
      )}

      {ownRanges && (
        <TextAreaField
          label="The load balancer’s addresses"
          hint="Addresses or ranges, one per line, none wider than /8 (IPv4) or /16 (IPv6). Only these may say who the visitor is, and they can never be banned."
          rows={4}
          spellCheck={false}
          value={rangesText}
          onChange={(event) => {
            setRangesText(event.target.value);
          }}
        />
      )}

      {choices.length > 1 && (
        <SelectField
          label="Read the visitor’s address from"
          value={header}
          onChange={(event) => {
            const value = event.target.value;
            setHeader(choices.find((choice) => choice === value) ?? '');
          }}
        >
          <option value="">{HEADER_LABEL[choices[0] ?? 'X-Forwarded-For']} (the usual one)</option>
          {choices.slice(1).map((choice) => (
            <option key={choice} value={choice}>
              {HEADER_LABEL[choice]}
            </option>
          ))}
        </SelectField>
      )}

      {BEHIND_CLOUDFLARE.has(mode) && domainNames.length > 0 && (
        <fieldset className="fieldset">
          <legend>How visitors reach each domain</legend>
          <p className="field-hint">
            One server can serve domains whose DNS goes through Cloudflare beside ones that point
            straight at it. Nginx trusts Cloudflare’s header only from Cloudflare, so both kinds log
            the right address either way; this says where a direct visitor is expected, so only a
            request that goes around Cloudflare to a domain behind it is reported.
          </p>
          {domainNames.map((name) => (
            <div key={name} className="site-group">
              <SelectField
                label={name}
                value={fronts[name] ?? 'cloudflare'}
                onChange={(event) => {
                  const front = event.target.value === 'direct' ? 'direct' : 'cloudflare';
                  setFronts((current) => ({ ...current, [name]: front }));
                }}
              >
                <option value="cloudflare">{FRONT_LABEL.cloudflare}</option>
                <option value="direct">{FRONT_LABEL.direct}</option>
              </SelectField>
            </div>
          ))}
        </fieldset>
      )}

      {candidates.length > 0 && (
        <fieldset className="fieldset">
          <legend>Nginx sites Bartizo may add its line to</legend>
          <p className="field-hint">
            Bartizo adds one marked line to each ticked site’s file: its log, for a site that keeps
            its own access log, and its bot blocks, once any bot is blocked. An unticked site is
            left as it is — it still logs through the server-wide log, but blocked bots reach it.
          </p>
          <p className="field-hint">
            A file can hold several blocks — the site, a <code>www.</code> or wildcard redirect, the
            default server. Tick the ones that serve pages; redirects seldom need it.
          </p>
          {byFile(candidates).map((group) => (
            <div key={group.file} className="site-group">
              <code className="site-group-file" title={group.file}>
                {shortFile(group.file)}
              </code>
              {group.sites.map((site, index) => {
                const detailId = `${idBase}-${group.file}-${String(index)}`;
                const details = siteDetails(site);
                return (
                  <div key={site.id} className="site-option">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={sites.includes(site.id)}
                        aria-describedby={details === '' ? undefined : detailId}
                        onChange={(event) => {
                          setSites((current) =>
                            event.target.checked
                              ? [...current, site.id]
                              : current.filter((each) => each !== site.id),
                          );
                        }}
                      />
                      {siteNames(site)}
                    </label>
                    {details !== '' && (
                      <span id={detailId} className="field-hint site-option-details">
                        {details}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </fieldset>
      )}

      <Alert tone="warning">
        Nginx or Apache is changed and reloaded as soon as the server takes this. Bartizo tests the
        configuration first and puts everything back if the web server refuses it.
      </Alert>
    </ConfirmDialog>
  );
}

function WithdrawDialog({
  serverId,
  onSaved,
  onCancel,
}: {
  serverId: string;
  onSaved: (view: ServerWebEnvironment) => void;
  onCancel: () => void;
}): ReactNode {
  const [error, setError] = useState<string | null>(null);
  const withdraw = useMutation({
    mutationFn: () => withdrawWebEnvironment(serverId),
    onSuccess: onSaved,
    onError: (caught: unknown) => {
      setError(
        needsReauth(caught)
          ? 'That needed a fresh confirmation. Close this and try again.'
          : caught instanceof Error
            ? caught.message
            : 'Could not stop it.',
      );
    },
  });

  return (
    <ConfirmDialog
      title="Stop managing the web environment?"
      description="Bartizo removes its real-IP trust, its access log and the lines it added to sites, and reloads the web server. Detection carries on."
      confirmLabel="Stop managing"
      danger
      busy={withdraw.isPending}
      error={error}
      onConfirm={() => {
        withdraw.mutate();
      }}
      onCancel={onCancel}
    />
  );
}
